import React, { useState } from "react";
import { toast } from "react-hot-toast";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import InstitutionSelect from "../../components/institutionSelect/InstitutionSelect";
import api from "../../utils/api";
import { LGAS, EXAM_CENTRES } from "../../data/abiriSupportLocations";
import { FIELD_OF_STUDY_GROUPS } from "../../data/nigerianInstitutions";
import "./SupportPage.css";

// The Abiri of Ile-Ife UTME JAMB Support 2027 — fully public page, no
// TutorsLinks account required. Sponsorship is by invitation only (Abiri
// family/friends, contacted individually) — no public sponsor form.

const MIN_AGE = 16;

const calcAge = (dob) => {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const maxDobForMinAge = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - MIN_AGE);
  return d.toISOString().split("T")[0];
};

const MAX_FILE_BYTES = 2 * 1024 * 1024;

const emptyApplicationForm = {
  fullName: "",
  dateOfBirth: "",
  gender: "",
  phone: "",
  email: "",
  address: "",
  lga: "",
  lgaOther: "",
  town: "",
  schoolName: "",
  currentClassOrGradYear: "",
  oLevelStatus: "",
  courseOfStudy: "",
  courseOfStudyOther: "",
  intendedInstitution: "",
  subject1: "",
  subject2: "",
  subject3: "",
  subject4: "",
  examCentrePreference: "",
  agreedToDeclaration: false,
};

function SupportPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [view, setView] = useState("overview"); // "overview" | "apply"

  // ── Candidate application ──
  const [appForm, setAppForm] = useState(emptyApplicationForm);
  const [passportPhoto, setPassportPhoto] = useState(null);
  const [oLevelResult, setOLevelResult] = useState(null);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [applicationSubmitting, setApplicationSubmitting] = useState(false);
  const [applicationError, setApplicationError] = useState("");

  const goApply = () => {
    setView("apply");
    window.scrollTo({ top: 0 });
  };
  const goHome = () => {
    setView("overview");
    window.scrollTo({ top: 0 });
  };

  const handleAppChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 11);
      setAppForm((f) => ({ ...f, phone: digitsOnly }));
      return;
    }

    setAppForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFilePick = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error("File must be 2MB or smaller.");
      e.target.value = "";
      return;
    }
    setter(file);
  };

  const validateApplication = () => {
    const required = [
      "fullName",
      "dateOfBirth",
      "gender",
      "phone",
      "address",
      "lga",
      "town",
      "schoolName",
      "currentClassOrGradYear",
      "oLevelStatus",
      "courseOfStudy",
      "intendedInstitution",
      "subject1",
      "subject2",
      "subject3",
      "subject4",
      "examCentrePreference",
    ];
    for (const key of required) {
      if (!appForm[key]?.toString().trim()) {
        return "Please fill in all required fields.";
      }
    }
    if (appForm.lga === "Others" && !appForm.lgaOther.trim()) {
      return "Please specify your Local Government Area of origin.";
    }
    if (appForm.courseOfStudy === "Other" && !appForm.courseOfStudyOther.trim()) {
      return "Please specify your course of study.";
    }
    if (!/^\d{10,11}$/.test(appForm.phone)) {
      return "Phone number must be 10-11 digits.";
    }
    if (calcAge(appForm.dateOfBirth) < MIN_AGE) {
      return `Applicants must be at least ${MIN_AGE} years old.`;
    }
    if (!passportPhoto) return "A passport photograph is required.";
    if (!appForm.agreedToDeclaration) {
      return "You must accept the declaration to apply.";
    }
    return "";
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateApplication();
    if (validationError) {
      setApplicationError(validationError);
      toast.error(validationError);
      return;
    }
    setApplicationError("");
    setApplicationSubmitting(true);
    try {
      const resolvedForm = {
        ...appForm,
        courseOfStudy:
          appForm.courseOfStudy === "Other"
            ? appForm.courseOfStudyOther.trim()
            : appForm.courseOfStudy,
      };
      delete resolvedForm.courseOfStudyOther;

      const formData = new FormData();
      Object.entries(resolvedForm).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append("passportPhoto", passportPhoto);
      if (oLevelResult) formData.append("oLevelResult", oLevelResult);

      await api.post("/support/applications", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setApplicationSubmitted(true);
      toast.success("Application submitted!");
      window.scrollTo({ top: 0 });
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to submit application.";
      setApplicationError(msg);
      toast.error(msg);
    } finally {
      setApplicationSubmitting(false);
    }
  };

  return (
    <>
      <Header
        isMenuOpen={isMenuOpen}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
      />
      <div className="sp-page">
        {view === "overview" ? (
          <>
            {/* ── Hero ── */}
            <section className="sp-hero">
              <div className="sp-section-inner sp-hero-grid">
                <div className="sp-hero-copy">
                  <span className="sp-eyebrow-pill">
                    The Royal Abiri of Ile-Ife, Osun State
                  </span>
                  <h1 className="sp-hero-title">
                    The Royal Abiri of Ile-Ife
                    <br />
                    UTME JAMB Support <span className="sp-gold">2027</span>
                  </h1>
                  <p className="sp-hero-sub">
                    Supporting 200+ qualified indigenes and residents of
                    Ile-Ife and its environs with payment of their 2027 UTME
                    JAMB registration fees.
                  </p>
                  <div className="sp-hero-prizes">
                    <span className="sp-hero-prizes-icon" aria-hidden="true">
                      🏆
                    </span>
                    <span>
                      Overall Top 3 win cash prizes — 1st ₦150,000 · 2nd
                      ₦100,000 · 3rd ₦50,000
                    </span>
                  </div>
                  <div className="sp-hero-stats">
                    <div className="sp-hero-stat">
                      <div className="sp-hero-stat-value">200+</div>
                      <div className="sp-hero-stat-label">
                        Candidates supported
                      </div>
                    </div>
                    <div className="sp-hero-stat">
                      <div className="sp-hero-stat-value">4</div>
                      <div className="sp-hero-stat-label">
                        Proposed exam venues
                      </div>
                    </div>
                  </div>
                  <div className="sp-hero-ctas">
                    <button type="button" className="sp-btn-primary" onClick={goApply}>
                      Apply as a Candidate
                    </button>
                  </div>
                </div>
                <div className="sp-hero-image">
                  <img
                    src="/images/abiri.jpeg"
                    alt="The Abiri of Ile-Ife UTME JAMB Support 2027 flier"
                  />
                </div>
              </div>
            </section>

            {/* ── Key Details / Support Package ── */}
            <section className="sp-section">
              <div className="sp-section-inner sp-col2">
                <div className="sp-card">
                  <h2 className="sp-card-title">Key Details</h2>
                  <div className="sp-detail-row">
                    <div className="sp-detail-label">Coverage</div>
                    <div className="sp-detail-value">
                      Ife South, Ife North, Ife Central &amp; Ife East
                    </div>
                  </div>
                  <div className="sp-divider" />
                  <div className="sp-detail-row">
                    <div className="sp-detail-label">
                      Proposed selection examination
                    </div>
                    <div className="sp-detail-value">
                      25–30 January 2027 · 10:00 AM
                    </div>
                  </div>
                  <div className="sp-divider" />
                  <button
                    type="button"
                    className="sp-btn-primary sp-btn-block"
                    onClick={goApply}
                  >
                    Apply Now
                  </button>
                </div>
                <div className="sp-card">
                  <h2 className="sp-card-title">Support Package</h2>
                  <div className="sp-detail-value">
                    UTME registration support for 200+ candidates
                  </div>
                  <div className="sp-divider" />
                  <div className="sp-detail-value">
                    4 proposed exam venues across Ile-Ife and its environs
                  </div>
                </div>
              </div>
            </section>

            {/* ── Enquiries & Sponsorship ── */}
            <section className="sp-section sp-section-alt">
              <div className="sp-section-inner sp-center">
                <div className="sp-eyebrow">Enquiries &amp; Sponsorship</div>
                <h2 className="sp-h2">Have questions, or want to sponsor a candidate?</h2>
                <div className="sp-enquiries-card sp-enquiries-card-standalone">
                  <div className="sp-enquiry-person">
                    <strong>Segun Abiri</strong> · Project Coordinator
                    <br />
                    0806 473 8240
                  </div>
                  <div className="sp-enquiry-person">
                    <strong>Ademola Abiri</strong> · Secretary
                    <br />
                    0816 238 2423
                  </div>
                </div>
              </div>
            </section>

            {/* ── Bottom CTA ── */}
            <section className="sp-cta-banner">
              <div className="sp-section-inner sp-cta-inner">
                <h2 className="sp-h2 sp-cta-title">
                  Are you a resident of or indigene of Ile-Ife and its
                  environs, preparing to write the UTME JAMB in 2027?
                </h2>
                <p className="sp-cta-sub">
                  Complete the application form. Selection examination holds
                  25–30 January 2027.
                </p>
                <button
                  type="button"
                  className="sp-btn-gold"
                  onClick={goApply}
                >
                  Apply Now
                </button>
              </div>
            </section>
          </>
        ) : (
          /* ── Application Form ── */
          <section className="sp-apply-section">
            <div className="sp-apply-inner">
              <button type="button" className="sp-back-link" onClick={goHome}>
                ← Back to overview
              </button>

              <div className="sp-apply-header">
                <div className="sp-eyebrow-light">The Abiri of Ile-Ife</div>
                <h1 className="sp-apply-title">JAMB UTME Support 2027</h1>
                <div className="sp-apply-subtitle">Application Form</div>
                <p className="sp-apply-desc">
                  Supporting 200+ qualified indigenes and residents of
                  Ile-Ife and its environs with payment of their 2027 UTME
                  JAMB registration fees.
                </p>
                <div className="sp-apply-meta">
                  <span>
                    <strong>Coverage:</strong> Ife South, Ife North, Ife
                    Central and Ife East
                  </span>
                  <span>
                    <strong>Selection Exam:</strong> 25–30 January 2027,
                    10:00 AM
                  </span>
                </div>
              </div>

              {applicationSubmitted ? (
                <div className="sp-apply-body sp-thank-you">
                  <div className="sp-thank-you-title">
                    Application submitted
                  </div>
                  <p className="sp-body-text">
                    Thank you — your application has been received. Shortlisted
                    applicants will be contacted with details of the written
                    selection examination.
                  </p>
                  <button
                    type="button"
                    className="sp-btn-primary"
                    onClick={goHome}
                  >
                    Back to overview
                  </button>
                </div>
              ) : (
                <form className="sp-apply-body" onSubmit={handleApplicationSubmit}>
                  <p className="sp-body-text">
                    Please complete the form below with accurate information.
                  </p>

                  <div>
                    <div className="sp-section-tag">
                      <span className="sp-tag-pill">SECTION 1</span>
                      <h2 className="sp-section-h2">Basic Information</h2>
                    </div>
                    <div className="sp-form-grid">
                      <label className="sp-field sp-field-full">
                        <span className="sp-field-label">Full Name</span>
                        <input
                          type="text"
                          name="fullName"
                          value={appForm.fullName}
                          onChange={handleAppChange}
                        />
                      </label>
                      <label className="sp-field">
                        <span className="sp-field-label">Date of Birth</span>
                        <input
                          type="date"
                          name="dateOfBirth"
                          max={maxDobForMinAge()}
                          value={appForm.dateOfBirth}
                          onChange={handleAppChange}
                        />
                      </label>
                      <div className="sp-field">
                        <span className="sp-field-label">Gender</span>
                        <div className="sp-radio-row">
                          <label className="sp-radio-pill">
                            <input
                              type="radio"
                              name="gender"
                              value="Male"
                              checked={appForm.gender === "Male"}
                              onChange={handleAppChange}
                            />
                            Male
                          </label>
                          <label className="sp-radio-pill">
                            <input
                              type="radio"
                              name="gender"
                              value="Female"
                              checked={appForm.gender === "Female"}
                              onChange={handleAppChange}
                            />
                            Female
                          </label>
                        </div>
                      </div>
                      <label className="sp-field">
                        <span className="sp-field-label">Phone Number</span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          name="phone"
                          maxLength={11}
                          value={appForm.phone}
                          onChange={handleAppChange}
                        />
                      </label>
                      <label className="sp-field">
                        <span className="sp-field-label">Email Address</span>
                        <input
                          type="email"
                          name="email"
                          value={appForm.email}
                          onChange={handleAppChange}
                        />
                      </label>
                      <label className="sp-field sp-field-full">
                        <span className="sp-field-label">
                          Residential Address
                        </span>
                        <input
                          type="text"
                          name="address"
                          value={appForm.address}
                          onChange={handleAppChange}
                        />
                      </label>
                      <label className="sp-field">
                        <span className="sp-field-label">
                          Local Government Area of Origin
                        </span>
                        <select
                          name="lga"
                          value={appForm.lga}
                          onChange={handleAppChange}
                        >
                          <option value="">Select LGA</option>
                          {LGAS.map((l) => (
                            <option key={l}>{l}</option>
                          ))}
                        </select>
                      </label>
                      {appForm.lga === "Others" && (
                        <label className="sp-field">
                          <span className="sp-field-label">
                            Please specify your LGA
                          </span>
                          <input
                            type="text"
                            name="lgaOther"
                            value={appForm.lgaOther}
                            onChange={handleAppChange}
                          />
                        </label>
                      )}
                      <label className="sp-field">
                        <span className="sp-field-label">
                          Town / Community
                        </span>
                        <input
                          type="text"
                          name="town"
                          value={appForm.town}
                          onChange={handleAppChange}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="sp-divider" />

                  <div>
                    <div className="sp-section-tag">
                      <span className="sp-tag-pill">SECTION 2</span>
                      <h2 className="sp-section-h2">Educational Information</h2>
                    </div>
                    <div className="sp-form-grid">
                      <label className="sp-field sp-field-full">
                        <span className="sp-field-label">
                          Name of Secondary School
                        </span>
                        <input
                          type="text"
                          name="schoolName"
                          value={appForm.schoolName}
                          onChange={handleAppChange}
                        />
                      </label>
                      <label className="sp-field">
                        <span className="sp-field-label">
                          Current Class / Year of Graduation
                        </span>
                        <input
                          type="text"
                          name="currentClassOrGradYear"
                          value={appForm.currentClassOrGradYear}
                          onChange={handleAppChange}
                        />
                      </label>
                      <label className="sp-field">
                        <span className="sp-field-label">O'Level Status</span>
                        <select
                          name="oLevelStatus"
                          value={appForm.oLevelStatus}
                          onChange={handleAppChange}
                        >
                          <option value="">Select status</option>
                          <option>Completed</option>
                          <option>Awaiting Result</option>
                          <option>Yet to Sit</option>
                        </select>
                      </label>
                      <label className="sp-field">
                        <span className="sp-field-label">
                          Intended Course of Study
                        </span>
                        <select
                          name="courseOfStudy"
                          value={appForm.courseOfStudy}
                          onChange={handleAppChange}
                        >
                          <option value="">Select Course of Study</option>
                          {FIELD_OF_STUDY_GROUPS.map((g) => (
                            <optgroup key={g.group} label={g.group}>
                              {g.fields.map((f) => (
                                <option key={f}>{f}</option>
                              ))}
                            </optgroup>
                          ))}
                          <option>Other</option>
                        </select>
                      </label>
                      {appForm.courseOfStudy === "Other" && (
                        <label className="sp-field">
                          <span className="sp-field-label">
                            Please specify your Course of Study
                          </span>
                          <input
                            type="text"
                            name="courseOfStudyOther"
                            value={appForm.courseOfStudyOther}
                            onChange={handleAppChange}
                          />
                        </label>
                      )}
                      <label className="sp-field">
                        <span className="sp-field-label">
                          Intended Institution
                        </span>
                        <InstitutionSelect
                          value={appForm.intendedInstitution}
                          onChange={(val) =>
                            setAppForm((f) => ({ ...f, intendedInstitution: val }))
                          }
                          placeholder="Search or select institution..."
                        />
                      </label>
                      <div className="sp-field sp-field-full">
                        <span className="sp-field-label">
                          UTME Subject Combination
                        </span>
                        <div className="sp-subjects-grid">
                          <input
                            type="text"
                            name="subject1"
                            placeholder="Subject 1"
                            value={appForm.subject1}
                            onChange={handleAppChange}
                          />
                          <input
                            type="text"
                            name="subject2"
                            placeholder="Subject 2"
                            value={appForm.subject2}
                            onChange={handleAppChange}
                          />
                          <input
                            type="text"
                            name="subject3"
                            placeholder="Subject 3"
                            value={appForm.subject3}
                            onChange={handleAppChange}
                          />
                          <input
                            type="text"
                            name="subject4"
                            placeholder="Subject 4"
                            value={appForm.subject4}
                            onChange={handleAppChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="sp-divider" />

                  <div>
                    <div className="sp-section-tag">
                      <span className="sp-tag-pill">SECTION 3</span>
                      <h2 className="sp-section-h2">Application Details</h2>
                    </div>
                    <span className="sp-field-label">
                      Examination Centre Preference
                    </span>
                    <div className="sp-radio-row sp-radio-wrap">
                      {EXAM_CENTRES.map((c) => (
                        <label className="sp-radio-pill" key={c}>
                          <input
                            type="radio"
                            name="examCentrePreference"
                            value={c}
                            checked={appForm.examCentrePreference === c}
                            onChange={handleAppChange}
                          />
                          {c}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="sp-divider" />

                  <div>
                    <div className="sp-section-tag">
                      <span className="sp-tag-pill">SECTION 4</span>
                      <h2 className="sp-section-h2">Uploads</h2>
                    </div>
                    <div className="sp-form-grid">
                      <div className="sp-upload-box">
                        <div className="sp-upload-title">
                          Recent Passport Photograph
                        </div>
                        <div className="sp-upload-hint">
                          JPG or PNG, max 2MB
                        </div>
                        <label className="sp-upload-btn">
                          {passportPhoto ? passportPhoto.name : "Attach / Upload"}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            hidden
                            onChange={handleFilePick(setPassportPhoto)}
                          />
                        </label>
                      </div>
                      <div className="sp-upload-box">
                        <div className="sp-upload-title">
                          O'Level Result{" "}
                          <span className="sp-optional">(Optional)</span>
                        </div>
                        <div className="sp-upload-hint">
                          PDF or image, max 2MB
                        </div>
                        <label className="sp-upload-btn">
                          {oLevelResult ? oLevelResult.name : "Attach / Upload"}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,application/pdf"
                            hidden
                            onChange={handleFilePick(setOLevelResult)}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="sp-divider" />

                  <div>
                    <h2 className="sp-section-h2">Declaration</h2>
                    <label className="sp-declaration">
                      <input
                        type="checkbox"
                        name="agreedToDeclaration"
                        checked={appForm.agreedToDeclaration}
                        onChange={handleAppChange}
                      />
                      <span>
                        I confirm that the information provided is true and
                        accurate. I understand that false or unverifiable
                        information may lead to disqualification.
                      </span>
                    </label>
                  </div>

                  <div className="sp-notice">
                    <div className="sp-notice-title">Important Notice</div>
                    <p className="sp-notice-text">
                      Submission of an application does not automatically
                      guarantee selection. Shortlisted applicants will be
                      contacted with details of the written selection
                      examination.
                    </p>
                  </div>

                  {applicationError && (
                    <p className="sp-error-text">{applicationError}</p>
                  )}

                  <button
                    type="submit"
                    className="sp-btn-primary sp-btn-block sp-btn-large"
                    disabled={applicationSubmitting}
                  >
                    {applicationSubmitting
                      ? "Submitting…"
                      : "Submit Application"}
                  </button>

                  <div className="sp-apply-enquiries">
                    <div className="sp-detail-label">Enquiries</div>
                    <div className="sp-form-grid sp-form-grid-2">
                      <div className="sp-enquiry-person">
                        <strong>Segun Abiri</strong>
                        <br />
                        <span className="sp-muted">Project Coordinator</span>
                        <br />
                        0806 473 8240
                      </div>
                      <div className="sp-enquiry-person">
                        <strong>Ademola Abiri</strong>
                        <br />
                        <span className="sp-muted">Secretary</span>
                        <br />
                        0816 238 2423
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </>
  );
}

export default SupportPage;
